/**
 * src/pages/FallacyLibrary/FallacyLibrary.tsx
 * Entry page for "Fallacy Library" in the Architecture of Knowledge initiative.
 *
 * Implements:
 * - KnowledgePageShell with pageId="fallacies", character="fallacies"
 * - Deep, searchable library of ALL 54 fallacies across 7 categories
 * - Search filter matching name, definition, keywords, aliases
 * - Category filter chips with live counts
 * - Expand all / Collapse all toggle
 * - Deep link support via hash (`#fallacy-<id>`) with smooth scrollToElement
 * - Fallacy Identification Lab (`id="identification-lab"`) with 26 statements
 * - RelatedLinks connected to Logic Laboratory, Critical Thinking Workshop,
 *   Knowledge Laboratory, and Glossary & References
 */

import React, { useState, useEffect, useMemo } from "react";
import { KnowledgePageShell } from "@/components/knowledge/KnowledgePageShell";
import { PageHero } from "@/components/knowledge/PageHero";
import { KSection } from "@/components/knowledge/KSection";
import { StatusKey } from "@/components/knowledge/StatusKey";
import { RelatedLinks } from "@/components/knowledge/RelatedLinks";
import { ALL_FALLACIES, CATEGORIES_META } from "./data";
import type { FallacyCategory } from "./types";
import { FallacyCard } from "./sections/FallacyCard";
import { IntroSection } from "./sections/IntroSection";
import { IdentificationLab } from "./sections/IdentificationLab";
import { scrollToElementAfterRender } from "@/lib/scroll";
import { Search, SlidersHorizontal, ChevronDown, ChevronUp, X, Filter } from "lucide-react";
import "@/components/cosmic/cosmic.css";
import "./fallacyLibrary.css";
export default function FallacyLibraryPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<FallacyCategory | "all">("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Deep-link hash resolution on mount or hash change
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#fallacy-")) {
        const fallacyId = hash.replace("#fallacy-", "");
        // Check if the fallacy is valid
        const target = ALL_FALLACIES.find(f => f.id === fallacyId);
        if (target) {
          // Reset filters if this fallacy would be filtered out
          setSelectedCategory(prevCat => {
            if (prevCat !== "all" && target.category !== prevCat) {
              return "all";
            }
            return prevCat;
          });
          setSearchQuery(prevQuery => {
            if (prevQuery.trim().length > 0) {
              return "";
            }
            return prevQuery;
          });
          // Auto-expand this item
          setExpandedIds(prev => new Set([...prev, fallacyId]));
          // Scroll smoothly to target using the project's scrollToElementAfterRender
          scrollToElementAfterRender(() => document.getElementById(`fallacy-${fallacyId}`), {
            block: "start",
            offset: 90
          });
        }
      } else if (hash === "#identification-lab") {
        scrollToElementAfterRender(() => document.getElementById("identification-lab"), {
          block: "start",
          offset: 90
        });
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Global click listener for in-page #fallacy-<id> anchor links
  // (Link.tsx calls e.preventDefault() for hash links so hashchange does not fire)
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = (e.target as Element | null)?.closest("a");
      if (!target) return;
      const href = target.getAttribute("href") || target.getAttribute("data-link-href") || "";
      // Handle #fallacy-<id>, /#fallacy-<id>, or fallacy-<id> from data-link-href
      const match = href.match(/(?:^|\/?#)fallacy-([a-zA-Z0-9_-]+)$/);
      if (!match) return;
      const fallacyId = match[1];
      const fallacyExists = ALL_FALLACIES.find(f => f.id === fallacyId);
      if (!fallacyExists) return;

      // (a) Reset searchQuery to "" and selectedCategory to "all" if the card would be filtered out
      setSelectedCategory(prevCat => {
        if (prevCat !== "all" && fallacyExists.category !== prevCat) {
          return "all";
        }
        return prevCat;
      });
      setSearchQuery(prevQuery => {
        if (prevQuery.trim().length > 0) {
          return "";
        }
        return prevQuery;
      });

      // (b) Add the id to expandedIds
      setExpandedIds(prev => new Set([...prev, fallacyId]));

      // (c) Update URL hash with history.replaceState (no extra scroll jump)
      window.history.replaceState(null, "", `#fallacy-${fallacyId}`);

      // (d) Scroll using existing scrollToElementAfterRender
      scrollToElementAfterRender(() => document.getElementById(`fallacy-${fallacyId}`), {
        block: "start",
        offset: 90
      });
    };
    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, []);

  // Filter and search logic
  const filteredFallacies = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return ALL_FALLACIES.filter(fallacy => {
      // Category filter
      if (selectedCategory !== "all" && fallacy.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (!q) return true;
      const matchesName = fallacy.name.toLowerCase().includes(q);
      const matchesDef = fallacy.definition.toLowerCase().includes(q);
      const matchesAliases = fallacy.aliases.some(a => a.toLowerCase().includes(q));
      const matchesKeywords = fallacy.keywords.some(k => k.toLowerCase().includes(q));
      const matchesReasoning = fallacy.reasoningProblem.toLowerCase().includes(q);
      return matchesName || matchesDef || matchesAliases || matchesKeywords || matchesReasoning;
    });
  }, [searchQuery, selectedCategory]);
  const handleToggleCard = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const handleExpandAll = () => {
    const allFiltered = new Set(filteredFallacies.map(f => f.id));
    setExpandedIds(allFiltered);
  };
  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };
  const handleClearSearch = () => {
    setSearchQuery("");
  };
  return <KnowledgePageShell pageId="fallacies" character="fallacies" accent="violet">
      <div className="fl-page">
        {/* Page Hero */}
        <PageHero eyebrow="Architecture of Knowledge — Module V" title="Logical Fallacy Library & Reasoning Diagnostic" subtitle="A comprehensive forensic reference cataloging 54 formal, informal, cognitive, and probabilistic reasoning breakdowns across science, philosophy, and human debate." />

        {/* Core Educational Content Container */}
        <KSection id="foundations" eyebrow="Epistemic Foundations" title="The Mechanics of Misleading Reasoning">
          <IntroSection />
        </KSection>

        {/* Fallacy Identification Lab Section */}
        <KSection id="lab-section" eyebrow="Interactive Diagnostic" title="Fallacy Identification & Argument Classification Lab" intro="Practice analyzing realistic fictional statements. Test your ability to distinguish clear formal and informal fallacies from weak inferences, genuine empirical evidence, unsupported claims, and ambiguous assertions.">
          <IdentificationLab />
        </KSection>

        {/* Master Fallacy Library Catalog Section */}
        <KSection id="catalog" eyebrow="Specimen Archive" title="The Complete 54-Fallacy Repository" intro="Explore formal syllogistic breakdowns, causal attribution errors, semantic ambiguities, and probabilistic biases. Filter by domain or search by keywords and aliases.">
          {/* Search Bar & Controls Bar */}
          <div className="ql-glass p-4 sm:p-6 rounded-xl border border-border mb-8 space-y-4">
            <div className="relative w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by fallacy name, keyword, alias (e.g. 'Bayes', 'straw man', 'correlation')..." className="fl-search-box" aria-label="Search fallacies" />
              {searchQuery && <button type="button" onClick={handleClearSearch} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer" aria-label="Clear search">
                  <X className="size-4" />
                </button>}
            </div>

              {/* Category Filter Chips */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-mono text-muted-foreground flex-wrap gap-1">
                  <span className="flex items-center gap-1.5 uppercase tracking-wider">
                    <Filter className="size-3.5 text-primary" aria-hidden="true" />
                    <span>Filter by Reasoning Domain</span>
                  </span>
                  <span>
                    Showing {filteredFallacies.length} of {ALL_FALLACIES.length} entries
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => setSelectedCategory("all")} className={`fl-filter-chip ${selectedCategory === "all" ? "active" : ""}`}>
                    <span>All Categories</span>
                    <span className="text-[11px] opacity-75">({ALL_FALLACIES.length})</span>
                  </button>

                  {CATEGORIES_META.map(cat => <button key={cat.id} type="button" onClick={() => setSelectedCategory(cat.id)} className={`fl-filter-chip ${selectedCategory === cat.id ? "active" : ""}`}>
                      <span>{cat.name}</span>
                      <span className="text-[11px] opacity-75">({cat.count})</span>
                    </button>)}
                </div>
              </div>

              {/* Expand / Collapse All Controls */}
              <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs font-mono flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={handleExpandAll} className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md border border-border/60 bg-card/40 hover:bg-card/80 text-foreground transition-all cursor-pointer min-h-[44px] touch-manipulation">
                    <ChevronDown className="size-3.5 text-primary" aria-hidden="true" />
                    <span>Expand All Matching</span>
                  </button>
                  <button type="button" onClick={handleCollapseAll} className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md border border-border/60 bg-card/40 hover:bg-card/80 text-foreground transition-all cursor-pointer min-h-[44px] touch-manipulation">
                    <ChevronUp className="size-3.5 text-muted-foreground" aria-hidden="true" />
                    <span>Collapse All</span>
                  </button>
                </div>

                {selectedCategory !== "all" && <span className="font-sans text-xs text-primary/90 italic break-words min-w-0">
                    Domain active:{" "}
                    {CATEGORIES_META.find(c => c.id === selectedCategory)?.description}
                  </span>}
              </div>
          </div>

          {/* Fallacy Cards List */}
          {filteredFallacies.length > 0 ? <div className="space-y-4">
              {filteredFallacies.map(fallacy => <FallacyCard key={fallacy.id} fallacy={fallacy} isExpanded={expandedIds.has(fallacy.id)} onToggleExpand={() => handleToggleCard(fallacy.id)} />)}
            </div> : <div className="ql-glass p-8 text-center rounded-xl border border-border/60 my-8">
              <Search className="size-8 text-muted-foreground mx-auto mb-3 opacity-50" aria-hidden="true" />
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground mb-1">
                No Matching Fallacies Found
              </h3>
              <p className="font-sans text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-4">
                No entries match the query "{searchQuery}" in the selected domain. Try broadening your search or resetting the category filter.
              </p>
              <button type="button" onClick={() => {
            setSearchQuery("");
            setSelectedCategory("all");
          }} className="ql-neon-btn px-4 py-2 rounded-full text-xs font-serif font-bold uppercase cursor-pointer">
                Reset All Filters
              </button>
            </div>}
        </KSection>

        {/* Epistemic Status Reference Key */}
        <KSection id="epistemic-status-key">
          <StatusKey />
        </KSection>

        {/* Connected Inquiries Footer */}
        <KSection id="connections" eyebrow="Interconnected Inquiry" title="Connected Reasoning Modules">
          <RelatedLinks title="Explore Related Knowledge Laboratories" items={[{
          to: "/LogicLaboratoryPage",
          label: "Logic Laboratory",
          why: "Construct formal truth tables, evaluate propositional connectives, and explore the mathematical boundary between valid deduction and invalid inference."
        }, {
          to: "/CriticalThinkingPage#argument-reconstructor",
          label: "Critical Thinking Workshop",
          why: "Deconstruct complex real-world arguments into premises and conclusions, reconstruct unstated hidden assumptions, and practice steelmanning."
        }, {
          to: "/KnowledgeLaboratoryPage",
          label: "Knowledge Laboratory",
          why: "Investigate epistemic justification, Gettier problems, fallibilism, and how empirical evidence grounds belief under radical uncertainty."
        }, {
          to: "/GlossaryAndReferencesPage",
          label: "Glossary & Philosophical References",
          why: "Browse formal definitions, historical context, and foundational citations across epistemology, mathematical logic, and the philosophy of science."
        }, {
          to: "/SimulationTheoryPage",
          label: "Simulation Theory Investigation",
          why: "Analyze Bostrom's trilemma through the lens of conditional probability, hypothesis falsifiability, and cosmological epistemic limits."
        }]} />
        </KSection>
      </div>
    </KnowledgePageShell>;
}